import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useMarketPolling } from '../../hooks/useMarketPolling';
import apiClient from '../../services/apiClient';
import GexChart from '../charts/GexChart';

const REGIME_COLORS: Record<string, { bg: string; border: string }> = {
  positive: { bg: 'rgba(76, 175, 80, 0.15)', border: 'rgba(76, 175, 80, 0.6)' },
  negative: { bg: 'rgba(244, 67, 54, 0.15)', border: 'rgba(244, 67, 54, 0.6)' },
  neutral: { bg: 'rgba(158, 158, 158, 0.12)', border: 'rgba(158, 158, 158, 0.4)' },
};

const GEXAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const { ticker: urlTicker } = useParams<{ ticker?: string }>();
  const [tickerInput, setTickerInput] = useState(urlTicker?.toUpperCase() || '');
  const [liveSpot, setLiveSpot] = useState<number | null>(null);
  const [positionRegimes, setPositionRegimes] = useState<Record<string, string>>({});
  const [positionTickers, setPositionTickers] = useState<string[]>([]);
  const [regimeLoading, setRegimeLoading] = useState(false);

  const activeTicker = urlTicker?.toUpperCase() || '';

  // Reset stale live price when ticker changes via URL
  useEffect(() => { setLiveSpot(null); }, [activeTicker]);

  // Load position tickers and their GEX regimes when no ticker selected
  useEffect(() => {
    if (activeTicker) return;
    setRegimeLoading(true);
    apiClient.get('/api/users/trades', { params: { status: 'open' } })
      .then((r) => {
        const tickers = Array.from(new Set<string>(r.data.map((t: any) => t.ticker)));
        setPositionTickers(tickers);
        if (tickers.length > 0) {
          return apiClient.get('/api/discover/gex-regimes', { params: { tickers: tickers.join(',') } })
            .then((res) => setPositionRegimes(res.data));
        }
      })
      .catch(() => {})
      .finally(() => setRegimeLoading(false));
  }, [activeTicker]);

  const handleTickerSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tickerInput.trim()) {
      setLiveSpot(null);
      navigate(`/portfolio/gamma-exposure/${tickerInput.trim().toUpperCase()}`, { replace: true });
    }
  };

  // Live price polling during market hours
  const pollTicker = useMemo(() => activeTicker ? [activeTicker] : [], [activeTicker]);
  useMarketPolling(pollTicker, useCallback((prices: Record<string, number>) => {
    const live = prices[activeTicker];
    if (live != null) setLiveSpot(live);
  }, [activeTicker]));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Gamma Exposure (GEX)
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Ticker"
          placeholder="e.g. AAPL"
          size="small"
          value={tickerInput}
          onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
          onKeyDown={handleTickerSubmit}
          sx={{ width: 160 }}
          autoComplete="off"
        />
      </Box>

      {activeTicker ? (
        <GexChart ticker={activeTicker} spotPriceOverride={liveSpot} />
      ) : (
        <Box>
          {regimeLoading && <CircularProgress size={20} sx={{ mb: 2 }} />}
          {positionTickers.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Your Positions</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {positionTickers.map((t) => {
                  const regime = positionRegimes[t] || 'neutral';
                  const colors = REGIME_COLORS[regime] || REGIME_COLORS.neutral;
                  return (
                    <Paper
                      key={t}
                      elevation={0}
                      sx={{
                        width: 80,
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        bgcolor: colors.bg,
                        border: `2px solid ${colors.border}`,
                        borderRadius: 2,
                        transition: 'transform 0.1s',
                        '&:hover': { transform: 'scale(1.08)' },
                      }}
                      onClick={() => navigate(`/portfolio/gamma-exposure/${t}`)}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>{t}</Typography>
                      <Typography variant="caption" sx={{ textTransform: 'capitalize', opacity: 0.8 }}>
                        {regime}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>

              {/* Legend */}
              <Box sx={{ mt: 3, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {[
                  { regime: 'positive', label: 'Positive GEX', desc: 'Dealers suppress moves — range-bound, mean reversion likely' },
                  { regime: 'negative', label: 'Negative GEX', desc: 'Dealers amplify moves — trending, directional risk' },
                  { regime: 'neutral', label: 'Neutral GEX', desc: 'Minimal dealer effect — normal market dynamics' },
                ].map((item) => {
                  const c = REGIME_COLORS[item.regime];
                  return (
                    <Box key={item.regime} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: c.bg, border: `2px solid ${c.border}`, flexShrink: 0, mt: '3px' }} />
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.2 }}>{item.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
          {!regimeLoading && positionTickers.length === 0 && (
            <Alert severity="info">Enter a ticker above and press Enter to view gamma exposure.</Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default GEXAnalysis;
